/**
 * Motion + one-off tokens for the first-run Welcome dialog.
 *
 * The dialog's animations are ported 1:1 from the hifi handoff
 * (`Welcome Dialog.dc.html`). We follow the repo's house style for
 * handoff micro-animation (`import-processing-indicator.tsx`,
 * `loading-spinner.tsx`): define the `@keyframes` in the top-level `sx` of the
 * dialog Paper so Emotion emits them once under their literal names, then
 * reference them by name from descendant elements via `animation: 'wdRise …'`.
 */

/**
 * Pale sage (`#e3ecca`, the handoff's `--gaia-green-pale-light`). This shade has
 * no MUI palette token — it is NOT `secondary.main` (`#c9da9e`) — so it lives
 * here as the single source of truth for the success-chip and step-card
 * icon-tile backgrounds.
 */
export const GREEN_PALE_LIGHT = '#e3ecca';

/**
 * Brand display stack (Effra Medium). Defined once with the other shared brand
 * tokens; re-exported here so the Welcome dialog's files import it alongside the
 * dialog's own motion tokens. See `auth-branding.ts` for the rationale.
 */
export { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding';

const EASE = 'cubic-bezier(.2,.8,.2,1)';

/**
 * Every welcome-dialog `@keyframes`, ported verbatim from the handoff. Spread
 * this object into the dialog Paper `sx`; Emotion registers the keyframes
 * globally under these names so any descendant (including plain SVG elements)
 * can reference them.
 */
export const WELCOME_KEYFRAMES = {
  '@keyframes wdIn': {
    from: { opacity: 0, transform: 'translateY(16px) scale(.97)' },
    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
  '@keyframes wdRise': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes wdLeaf': {
    '0%': { opacity: 0, transform: 'scale(.15) rotate(-40deg)' },
    '58%': { opacity: 1, transform: 'scale(1.12) rotate(6deg)' },
    '80%': { transform: 'scale(.97) rotate(-2deg)' },
    '100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
  },
  '@keyframes wdGlow': {
    '0%': { opacity: 0, transform: 'scale(.5)' },
    '58%': { opacity: 0.85, transform: 'scale(1.15)' },
    '100%': { opacity: 0.55, transform: 'scale(1)' },
  },
  '@keyframes wdRing': {
    from: { strokeDashoffset: 282.7 },
    to: { strokeDashoffset: 0 },
  },
  '@keyframes wdFloat': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-6%)' },
  },
} as const;

/** Entrance for a staggered element: fade + rise, holding the start and end state. */
export const rise = (delay: string): string => `wdRise .5s ${EASE} ${delay} both`;

/**
 * Per-element entrance delays: the stagger ladder from the handoff, extended
 * for the five Get-started steps (the handoff had three cards).
 */
export const WELCOME_DELAYS = {
  chip: '.45s',
  heading: '.55s',
  subcopy: '.65s',
  cards: ['.8s', '.9s', '1s', '1.1s', '1.2s'],
  action: '1.32s',
  lockup: '.95s',
} as const;
